import { DndContext, MeasuringStrategy, useDroppable } from '@dnd-kit/core'
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { mdiDragVertical } from '@mdi/js'
import Icon from '@mdi/react'
import Button from '@mui/material/Button'
import { TrainingVideo, VideoTrainingSection } from 'admin/interfaces'
import { deleteVideo, getTrainingVideos, reorderVideos, saveSection, saveVideo } from 'admin/services/training/net'
import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router'
import { firstValueFrom } from 'rxjs'
import { message } from 'services/snackbar'
import { reorder, sortByKeyAscending } from 'shared/utils'
import { StoreThunkDispatch } from 'store/state'
import styles from './SectionList.pcss'

interface SectionDetailsProps {
  sections: VideoTrainingSection[]
}

export const SectionDetails: React.FC<SectionDetailsProps> = ({ sections }) => {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const params = useParams()
  const navigate = useNavigate()
  const id = params.sectionId

  const { setNodeRef: setDroppableNodeRef } = useDroppable({
    id: 'droppable'
  })

  const section = sections.find((s) => s.id.toString() === id)
  const [newSectionTitle, setNewSectionTitle] = useState(section?.title || '')
  const [newSectionSlug, setNewSectionSlug] = useState(section?.slug || '')
  const [newSectionLabel, setNewSectionLabel] = useState(section?.label || '')
  const [newVideo, setNewVideo] = useState({ title: '', url: '', thumbnail: '', actionUrl: '' })
  const [loading, setLoading] = useState(false)
  const [vids, setVids] = useState<TrainingVideo[]>([])
  const [order, setOrder] = useState(vids.map((v) => v.id))

  useEffect(() => {
    setOrder(vids.map((p) => p.id))
  }, [vids])

  const fetchVideos = useCallback(() => {
    if (id) {
      firstValueFrom(dispatch(getTrainingVideos(id, true)))
        .then((response) => {
          setVids(response)
        })
        .catch((err) => {
          console.log(err)
          dispatch(message(`Error fetching videos: ${err.message}`, 'error'))
        })
    }
  }, [dispatch, id])

  const saveUpdates = () => {
    const title = newSectionTitle.trim()
    if (!title) {
      dispatch(message('Section title cannot be empty'))
      return
    }

    setLoading(true)
    const updated = { title, slug: newSectionSlug, label: newSectionLabel }
    firstValueFrom(dispatch(saveSection(updated, id, section?.order?.toString())))
      .then(() => {
        dispatch(message('Section updated!'))
      })
      .catch((err) => {
        console.log(err)
        dispatch(message(`Error: ${err.message}`, 'error'))
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const addVideo = React.useCallback((
    title: string,
    url: string,
    thumbnailUrl: string,
    sectionId: string,
    order?: string,
    id?: string,
    actionUrl?: string
  ) => {
    firstValueFrom(dispatch(saveVideo(title, url, thumbnailUrl, sectionId, id, order, actionUrl)))
      .then(() => {
        dispatch(message(`Video ${id ? 'updated' : 'created'}!`))
        setNewVideo({ title: '', url: '', thumbnail: '', actionUrl: '' })
      })
      .catch((err) => {
        console.log(err)
        dispatch(message(`Error: ${err.message}`, 'error'))
      })
      .finally(fetchVideos)
  }, [dispatch, fetchVideos])

  const handleAddVideo = () => {
    const { title, url, thumbnail } = newVideo
    if (!title || !url || !thumbnail) {
      dispatch(message('Please enter a title, video URL, and thumbnail URL'))
      return
    }

    if (section) {
      addVideo(
        newVideo.title,
        newVideo.url,
        newVideo.thumbnail,
        section.id,
        vids.length?.toString(),
        undefined,
        newVideo.actionUrl
      )
    }
  }

  React.useEffect(() => {
    fetchVideos()
  }, [fetchVideos])

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleAddVideo()
    }
  }

  const back = () => {
    navigate('/admin/onboarding/video-training')
  }

  const onDragEnd = (event: any) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oldIndex = order.indexOf(active.id)
      const newIndex = order.indexOf(over.id)
      const reordered = reorder(order, oldIndex, newIndex)
      setOrder(reordered)
      firstValueFrom(dispatch(reorderVideos('item', reordered.map(id => parseInt(id, 10)))))
        .then(() => {
          dispatch(message('Videos reordered!'))
        })
        .catch((e) => {
          console.log('[error]', e)
          dispatch(message(`Reordering error: ${e.message}`))
        })
    }
  }

  if (!section) {
    return <h2>Section not found!</h2>
  }

  const updateVideo = (id: string, text: string, videoUrl: string, thumbnailUrl: string, actionUrl: string) => {
    firstValueFrom(dispatch(saveVideo(text, videoUrl, thumbnailUrl, section.id, id, undefined, actionUrl)))
      .then(() => {
        dispatch(message('Video edited!'))
      })
      .catch((e) => {
        console.log('[error]', e)
        dispatch(message(`Editing error: ${e.message}`))
      })
  }

  const onDeleteVideo = (id: string) => {
    firstValueFrom(dispatch(deleteVideo(id)))
      .then(() => {
        fetchVideos()
        dispatch(message('Video deleted!'))
      })
      .catch((e) => {
        console.log('[error]', e)
        dispatch(message(`Delete error: ${e.message}`))
      })
  }

  return (
    <div className={styles['category-details-container']}>
      <div><Button className={styles['back-button']} onClick={back}>Back</Button></div>
      <h3 className={styles.subtitle}>Section title</h3>
      <div className={`${styles.row} ${styles['row-col']}`}>
        <div>
          <input
            type="text"
            value={newSectionTitle}
            placeholder="Section Name"
            onChange={(e) => setNewSectionTitle(e.target.value)}
          />
        </div>
        <div>
          <input
            type="text"
            value={newSectionLabel}
            placeholder="Section Label"
            onChange={(e) => setNewSectionLabel(e.target.value)}
          />
        </div>
        <div>
          <input
            type="text"
            value={newSectionSlug}
            placeholder="Section Slug"
            onChange={(e) => setNewSectionSlug(e.target.value)}
          />
        </div>
        <Button
          disabled={loading}
          variant="contained"
          color="primary"
          onClick={saveUpdates}
          className={styles['save-button']}
        >
          Save
        </Button>
      </div>
      <h3 className={styles.subtitle}>Videos</h3>
      <DndContext
        onDragEnd={onDragEnd}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      >
        <SortableContext items={order} strategy={rectSortingStrategy}>
          <ul ref={setDroppableNodeRef}>
            {vids
              .sort(sortByKeyAscending('order'))
              .map((vid) => (
                <VideoListItem
                  key={vid.id}
                  data={vid}
                  onUpdate={updateVideo}
                  onDelete={onDeleteVideo}
                />
              ))}
          </ul>
        </SortableContext>
      </DndContext>
      <div className={styles['create-category']}>
        <h3 className={styles.subtitle}>Add New Video</h3>
        <input
          type="text"
          placeholder="Title"
          value={newVideo.title}
          onChange={(e) => setNewVideo(current => ({ ...current, title: e.target.value }))}
          onKeyDown={onKeyDown}
        />
        <input
          type="text"
          placeholder="Video URL"
          value={newVideo.url}
          onChange={(e) => setNewVideo(current => ({ ...current, url: e.target.value }))}
        />
        <input
          type="text"
          placeholder="Thumbnail URL"
          value={newVideo.thumbnail}
          onChange={(e) => setNewVideo(current => ({ ...current, thumbnail: e.target.value }))}
        />
        <input
          type="text"
          placeholder="Action URL"
          value={newVideo.actionUrl}
          onChange={(e) => setNewVideo(current => ({ ...current, actionUrl: e.target.value }))}
        />
        <Button variant="contained" color="primary" onClick={handleAddVideo}>Add</Button>
      </div>
    </div>
  )
}

interface VideoListItemProps {
  data: TrainingVideo
  onUpdate: (id: string, text: string, videoUrl: string, thumbnailUrl: string, actionUrl: string) => void
  onDelete: (id: string) => void
}
function VideoListItem({ data, onUpdate, onDelete }: VideoListItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: data.id })
  const style = {
    transform: CSS.Translate.toString(transform),
    zIndex: isDragging ? '2' : '1',
    transition,
    position: isDragging ? 'relative' : 'static',
    cursor: isDragging ? 'grabbing' : 'grab'
  } as any

  const [title, setTitle] = useState(data.title)
  const [url, setURL] = useState(data.url)
  const [thumbnailUrl, setThumbnailUrl] = useState(data.thumbnailUrl)
  const [actionUrl, setActionUrl] = useState(data.actionUrl || '')

  const onTitleTextChange = (e: any) => {
    setTitle(e.target.value)
  }

  const onURLTextChange = (e: any) => {
    setURL(e.target.value)
  }

  const onThumbnailTextChange = (e: any) => {
    setThumbnailUrl(e.target.value)
  }

  const onActionUrlTextChange = (e: any) => {
    setActionUrl(e.target.value)
  }

  const handleDelete = () => {
    onDelete(data.id)
  }

  const handleUpdate = () => {
    onUpdate(data.id, title, url, thumbnailUrl, actionUrl)
  }

  return (
    <li key={data.id} className={styles.row} ref={setNodeRef} style={style}>
      <div className={styles.handle} {...listeners} {...attributes}>
        <Icon path={mdiDragVertical} size="24px" color="#333" />
      </div>
      <div className={styles['li-content']}>
        <div className={styles.label}>Video title</div>
        <textarea
          value={title}
          placeholder="Video title"
          onChange={onTitleTextChange}
        />
        <div className={styles.label}>Video URL</div>
        <input placeholder="Video URL" className={styles.input} value={url} onChange={onURLTextChange} />
        <div className={styles.label}>Thumbnail URL</div>
        <input placeholder="Thumbnail URL" className={styles.input} value={thumbnailUrl} onChange={onThumbnailTextChange} />
        <div className={styles.label}>Action URL</div>
        <input placeholder="Action URL" className={styles.input} value={actionUrl} onChange={onActionUrlTextChange} />
        <img src={thumbnailUrl} alt={title} className={styles.thumb} />
      </div>
      <Button variant="contained" color="primary" onClick={handleUpdate}>
        Update
      </Button>
      <Button onClick={handleDelete} className={styles['delete-button']}>
        Delete
      </Button>
    </li>
  )
}
